/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div v-if="audioSupported && hasMicrophone">
    <div
      v-if="recordingStatus === READY"
      class="form-group"
    >
      <h2
        id="recordheader"
      >
        Record
      </h2>
      <br>
      <div v-if="isFreeTrial">
        Ready to record? Grab one of your scripts or think of a message you'd like to
        rehearse, and let's get started!<br><br>

        When you click the green Record button, the site will show a countdown:
        3-2-1 to help you get ready.  Remember to allow access to your microphone
        if a dialogue box appears, record in a quiet place, and try to keep your
        speech to 10 minutes or less. If you need to stop and re-record,
        feel free - you can can click Cancel to retry your recording as much
        as you like.<br><br>
      </div>
      <div v-else>
        To start recording, click Record.<br><br>
        Record in a quiet place for optimal recording quality.<br><br>
        You must click &quot;Allow&quot; if an authorization dialog appears.<br><br>
        Recording max length is 15 minutes.<br><br>
      </div>
      <span class="sr-only">
        You may wish to wear headphones so your screen reader is not recorded
        by your microphone. There will be a 3 second countdown and then recording
        will begin. The Stop button will automatically be focused
        so you can easily stop the recording by hitting enter. When the recording
        has been stopped you will be asked to provide a title.
      </span>
      <a
        id="recordIconContainer"
        tabindex="0"
        @keypress="recordClicked"
        @click="recordClicked"
      >
        <svg class="icon-record">
          <use xlink:href="/static/svg-symbols.svg#icon-record" />
        </svg>
        <span>Record</span>
      </a>
    </div>

    <div
      v-if="recordingStatus === AUTHORIZING"
      class="form-group"
    >
      <br><br>
      <svg
        class="icon-refresh rotate"
        style="width: 19px; height: 19px;"
      >
        <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
      </svg>
      <span style="font-size: 19px; position:relative; bottom: 5px;">
        Preparing to record
      </span>
      <br><br>
      If your recording has not yet started, you may need to click &quot;Allow&quot; to authorize access to
      your microphone.
      <br><br>
      <span v-if="linkEnabled">
        For extra troubleshooting steps, see
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="helpLink" />
      </span>
    </div>

    <div
      v-if="recordingStatus === COUNT_DOWN"
      class="form-group"
    >
      <div class="padded-row row">
        <span style="font-size: 22pt;">
          Recording starts in
        </span><br>
        <span style="font-size: 42pt; padding-left: 93px;">
          {{ countDownTimer.count }}
        </span>
      </div>
    </div>

    <div
      v-if="recordingStatus === RECORDING"
      class="form-group"
    >
      <div class="row">
        <h2 style="padding-left: 20px; font-size: 14px;">
          Recording ...
        </h2>
        <div style="display: inline-block; margin: 10px 10px 0 20px; width: 125px;">
          <microphone-icon />
        </div>
        <div class="duration-container-outer">
          <div class="duration-container-inner">
            Duration: {{ recordingDuration }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <a
            id="stopIconContainer"
            tabindex="0"
            @keypress="stopClicked"
            @click.prevent="stopClicked"
          >
            <svg class="icon-record">
              <use xlink:href="/static/svg-symbols.svg#icon-record" />
            </svg>
            <span>STOP</span>
          </a>
        </div>
      </div>
    </div>

    <div
      v-if="recordingStatus === STOPPING"
      class="form-group"
    >
      <div class="padded-row row">
        <h2>
          Stopping...&nbsp;&nbsp;
          <svg
            class="icon-refresh rotate large-spinner"
          >
            <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
          </svg>
        </h2>
      </div>
    </div>

    <div
      v-if="recordingStatus === STOPPED"
      class="form-group"
    >
      <form
        aria-label="Name Your Recording"
        @submit.prevent="nextClicked"
      >
        <div v-if="durationTimer.count >= 50">
          <div class="form-group">
            <strong>Recording duration: {{ recordingDuration }}</strong>
            <br><br>
            <assignment-select
              v-if="showAssignmentSelect"
              @assignmentChosen="assignmentChosen"
            />
            <label
              for="recordingTitle"
              class="field-label"
            >
              Title
            </label>
            <input
              id="recordingTitle"
              ref="recordingTitle"
              v-model.trim="formValues.title"
              type="text"
              class="form-control"
              placeholder="title"
              name="title"
              required
              maxlength="50"
            >
            <div
              v-if="speakerGroups.length > 1 && !assignmentsEnabled"
            >
              <br>
              <label
                class="field-label"
                for="groupSelector"
              >
                <span v-if="orgType == 'edu'">
                  Class
                </span>
                <span v-if="orgType != 'edu'">
                  Group
                </span>
              </label>
              <select
                id="groupSelector"
                v-model="selectedGroup"
                class="form-control"
              >
                <option
                  v-for="group in speakerGroups"
                  :value="group"
                >
                  {{ group.name }}
                </option>
              </select>
            </div>

            <speaker-select
              v-if="showAlgorithmSelect"
              @algorithmChosen="algorithmChosen"
            />

            <assignment-sharing
              v-if="assignmentsEnabled && recordingType !== 'private'"
              ref-num="4"
              @sharingChanged="sharingChanged"
            />
            <br>
            <debug-recordings
              v-if="$store.getters.org && $store.getters.org.settings && $store.getters.org.settings.Debug_Recording_Options"
              @devFormData="loadDevFormData"
            />
          </div>
          <button
            class="btn clear-btn"
            style="margin-right: 50px;"
            type="button"
            @click="cancelClicked"
          >
            Cancel
          </button>
          <button
            class="btn btn-primary"
            type="submit"
          >
            Save
          </button>
        </div>
        <div v-if="durationTimer.count < 50">
          <div
            :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
            class="form-group"
            role="alert"
          >
            Your recording is only {{ Math.floor(durationTimer.count / 10) }} second(s) long. Recordings shorter
            than 5 seconds long cannot be submitted.
            <br>
          </div>
          <button
            class="btn btn-primary"
            type="button"
            @click="reset()"
          >
            Go Back
          </button>
        </div>
      </form>
    </div>
    <div
      v-if="recordingStatus === UPLOADING"
      class="padded-row form-group"
    >
      <h2>
        Uploading recording&nbsp;&nbsp;
        <svg
          class="icon-refresh rotate large-spinner"
        >
          <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
        </svg>
      </h2>
    </div>
    <div
      v-if="recordingStatus === AUTHORIZATION_ERROR"
      class="form-group"
    >
      <span
        role="alert"
        :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
      >
        You may need to modify your Microphone/Privacy Settings to grant your browser access to your microphone.
        To assist you with fixing your microphone settings, please review
        <span v-html="helpLink" />
      </span>
    </div>
    <div
      v-if="recordingStatus === SILENCE_ERROR"
      class="form-group"
    >
      <span
        v-if="isIos"
        role="alert"
        :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
      >
        Recording failed. On iOS devices, an extra step is now needed to fix recording. Please either
        force-close Safari then reopen it or open VoiceVibes in a new tab. Simply clicking
        reload will not restore microphone access.<br><br>
        For detailed instructions, please review
        <span v-html="helpLink" />
      </span>
      <span
        v-else
        role="alert"
        :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
      >
        Your browser was not able to access your microphone and was capturing only silence. Please ensure you have
        the right input device selected in your computer and browser settings then restart your browser. To assist you with fixing your microphone settings, please review
        <span v-html="helpLink" />
      </span>
    </div>
    <audio id="buzzerNoise">
      <source
        id="buzzerNoiseSource1"
        :src="(isSafari) ? '/static/silence.ogg' : '/static/buzzer.ogg'"
        type="audio/ogg"
      >
      <source
        id="buzzerNoiseSource2"
        :src="(isSafari) ? '/static/silence.mp3' : '/static/buzzer.ogg'"
        type="audio/mpeg"
      >
      Your browser does not support the audio element.
    </audio>
  </div>
  <div
    v-else-if="audioSupported && !hasMicrophone"
    role="alert"
  >
    You're using a browser that supports recording audio but your browser cannot access your microphone.
    If you're certain that you have a microphone installed then this is likely a permissions problem. You
    may need to modify your Microphone/Privacy Settings to grant your browser access to your microphone.
    <span v-if="isEdge">
      In Windows, you can locate these settings via Start-&gt;Settings-&gt;Privacy-&gt;Microphone.
      Ensure that the checkboxes for "Let apps use my microphone" and "Microsoft Edge" are both ON.
    </span>
  </div>
  <div
    v-else-if="!audioSupported && isIos && !isSafari"
    :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
    role="alert"
  >
    You appear to be using a browser on iOS that does not support recording audio from your device's microphone.
    Please use Safari on iOS version 11 or above to enable recording on this device.
  </div>
  <div
    v-else-if="!audioSupported && isIos"
    :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
    role="alert"
  >
    You appear to be using a browser on iOS that does not support recording audio from your device's microphone.
    Please use Safari on iOS version 11 or above to enable recording on this device.<br><br>
    If you are already using Safari on iOS 11, please force-close Safari then reopen it to fix the issue.<br><br>
    For additional details, please review
    <span v-html="helpLink" />
  </div>
  <div
    v-else-if="!audioSupported"
    :style="(!isEmbedded) ? 'font-size: 22pt;' : ''"
    role="alert"
  >
    You appear to be using a browser that does not support recording audio from your device's microphone.
    To record a speech, please use a recent version of Chrome, Firefox, Safari or Microsoft Edge.
  </div>
</template>

<style lang="less" scoped>
  h2 {
    margin: 5px 0;
  }
  #recordingTitle {
    margin-bottom: 20px;
  }
  #recordingTitle, #groupSelector {
    max-width: 300px;
  }
  .duration-container-outer {
    display: inline-block;
    .duration-container-inner {
      font-size: 12px;
      font-weight: 400;
      display: table-cell;
      vertical-align: middle;
      height: 120px;
      padding: 0 0 0 12px;
    }
  }
  .padded-row {
    padding-left: 30px;
  }

  .icon-microphone {
    height: 125px; width: 125px;
  }

  .icon-record {
    width: 23px; height: 23px;
  }

  input[type=range] {
    margin-top: 4px;
  }
  #recordIconContainer {
    display: inline-block;
    cursor: pointer;
    color: #F26724;

    .icon-microphone {
      display: inline-block;
    }
    span {
      position: relative;
      bottom: 6px;
    }
  }
  #groupSelector {
    margin-bottom: 20px;
  }
  #stopIconContainer:focus, stopIconContainer:active {
    text-decoration: none;
  }
  #stopIconContainer {
    display: inline-block;
    cursor: pointer;
    margin: 23px 0 0 6px;
    padding: 10px;

    .svgicon {
      display: inline-block;
      stroke-width: 0;
      stroke: currentColor;
      fill: currentColor;
    }
    span {
      font-size: 14px;
      position: relative;
      bottom: 5px;
    }
  }
</style>

<script>
import service from '../../services/recordings';
import userApi from '../../services/user';
import RecordRTC from 'expose-loader?RecordRTC!recordrtc/RecordRTC.min.js';
import DetectRTC from 'expose-loader?DetectRTC!detectrtc/DetectRTC.min.js';
// TODO: Replace this with https://github.com/mattdiamond/Recorderjs
import Recorder from 'expose-loader?Recorder!../specials/recorder.js';
import util from '../utils/util';
//import download from 'expose-loader?download!downloadjs/download.js'; // eslint-disable-line no-unused-vars
import AssignmentRecording from '../specials/AssignmentRecording';
import AssignmentSelect from '../specials/AssignmentSelect';
import AssignmentSharing from '../specials/AssignmentSharing';
import SpeakerSelect from '../specials/SpeakerSelect';
import MicrophoneIcon from '../specials/MicrophoneIcon';
import DebugRecordings from '../specials/DebugRecordings';

export default {
  components: {SpeakerSelect, AssignmentSelect, AssignmentSharing, MicrophoneIcon, DebugRecordings},
  mixins: [AssignmentRecording],
  data() {
    return {
      // recordingStatus Constants
      recordingStatus: 1,
      READY: 1,
      AUTHORIZING: 2,
      COUNT_DOWN: 3,
      RECORDING: 4,
      STOPPING: 5,
      STOPPED: 6,
      UPLOADING: 7,
      AUTHORIZATION_ERROR: -1,
      SILENCE_ERROR: -2,
      RECORDING_MAX_LENGTH: 9000, // 15 minutes

      // Other variables
      mediaRecorder: null,
      audioSupported: util.hasWebRTC,
      isEdge: DetectRTC.browser.isEdge,
      isIos: util.isIos,
      isSafari: util.isSafari,
      recordingMethod: util.recordingMethod,
      hasMicrophone: false,
      linkEnabled: false,
      mediaFile: null,
      processor: null,
      recorder: null,
      microphone: null,
      audioContext: null,
      helpDocForIos: 'https://docs.google.com/document/d/e/2PACX-1vQGfBYrduIM3wP2GnWoWjlmfcjfPiGyfESEfj2svykiCjI8AvNEEe7UUBqOHHO0QxpwsBi57y3xCHe2/pub',
      helpDocForAll: 'https://voicevibes.s3-us-west-2.amazonaws.com/resources/Troubleshooting-Recording-Errors-in-VoiceVibes.pdf',
      selectedGroup: null,
      noVolumeConsecutiveCount: 0,
      countDownTimer: {
        count: 4,
        id: null,
      },
      durationTimer: {
        start: null,
        count: 0,
        id: null,
      },
      formValues: {
        title: '',
        submitted: false,
        source: 'ui record form',
        feedbackRequested: false,
        algorithm: null,
      }
    };
  },
  computed: {
    isEmbedded: {
      cache: false,
      get() {
        return this.$route.name !== 'record';
      }
    },
    helpLink() {
      const url = (this.isIos) ? this.helpDocForIos : this.helpDocForAll;
      return `<a target="_blank" href="${url}">Troubleshooting Recording Errors in VoiceVibes</a>`;
    },
    speakerGroups() {
      let speakerGroups = this._.filter(this.$store.getters.groups, group => {
        return this._.includes(group.roles, 'speaker') && group.membershipStatus === 'active';
      });
      speakerGroups = this._.sortBy(
        speakerGroups, g => -new Date(g.createdDate).getTime()
      );

      // Set default selectedGroup
      if (!this.selectedGroup && speakerGroups.length) {
        /* eslint-disable-next-line no-side-effects-in-computed-properties */
        this.selectedGroup = speakerGroups[0];
      }

      return speakerGroups;
    },
    recordingDuration() {
      let mins = Math.floor(this.durationTimer.count / 600),
        secs = Math.floor((this.durationTimer.count / 10) % 60),
        msecs = Math.floor(this.durationTimer.count % 10);

      if (mins < 10) {
        mins = `0${mins}`;
      }
      if (secs < 10) {
        secs = `0${secs}`;
      }

      return `${mins}:${secs}.${msecs}`;
    },
    orgType() {
      return this.$store.getters.org && this.$store.getters.org.type;
    },
    isFreeTrial() {
      return !!(this.user && this.user.metadata && this.user.metadata.freeTrial);
    },
    user() {
      return this.$store.getters.user;
    }
  },
  created() {
    // Polyfill Safari
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    DetectRTC.load(() => {
      this.hasMicrophone = DetectRTC.hasMicrophone;
    });
  },
  methods: {
    closeRecording() {
      if (this.processor) {
        this.processor.disconnect();
      }
      this.processor = null;

      if (this.audioContext) {
        this.audioContext.close();
      }
      this.audioContext = null;

      if (window._audioStream) {
        window._audioStream.getTracks().forEach(track => track.stop());
        window._audioStream = null;
      }
    },
    drawAudio(volume) {
      let elementVolume = document.getElementById('micIconSvg');
      if (!elementVolume) {
        return true;
      }
      let percent = 100 - Math.floor(volume / 12 * 100) + '%';
      document.getElementById('micIconClip').setAttribute('y', percent);
    },

    analyzer(context) {
      let listener = context.createAnalyser();
      this.microphone.connect(listener);
      listener.fftSize = 256;
      let bufferLength = listener.frequencyBinCount;
      let analyserData = new Uint8Array(bufferLength); // eslint-disable-line no-undef
      const noVolumeConsecutiveCountThreshold = (this.isIos) ? 290 : 600;

      let getVolume = () => {
        const isRecording = !!(this.recordingStatus === this.AUTHORIZING ||
            this.recordingStatus === this.COUNT_DOWN || this.recordingStatus === this.RECORDING);

        // Exit early if we're finished or stopping
        if (!isRecording) {
          return true;
        }

        let volumeSum = 0;

        listener.getByteFrequencyData(analyserData);

        for (let i = 0; i < bufferLength; i++) {
          volumeSum += analyserData[i];
        }

        let volume = volumeSum / bufferLength;
        if (this.noVolumeConsecutiveCount !== null) {
          if (this.noVolumeConsecutiveCount >= noVolumeConsecutiveCountThreshold) {
            // Stop the recording and show an error after 6 seconds of no audio
            this.showErrorScreen(this.SILENCE_ERROR);
            return true;
          } else if (volume === 0) {
            this.noVolumeConsecutiveCount++;
          } else {
            // As soon as we get a signal, we stop checking if the mic is bad
            this.noVolumeConsecutiveCount = null;
          }
        }
        this.drawAudio(volume / 10);

        return setTimeout(() => {
          if (isRecording) {
            getVolume();
          }
        }, 10);
      };
      getVolume();
    },
    intervalTick() {
      // Because the timer may not run every second, we cannot
      // simply increment durationTimer.count
      const now = Math.round((new Date()).getTime() / 100);
      this.durationTimer.count = now - this.durationTimer.start;

      if (this.durationTimer.count >= this.RECORDING_MAX_LENGTH) {
        this.stopClicked();
      }
    },
    reset(recordingStatus=this.READY, fullReset=true) {
      this.recordingStatus = recordingStatus;

      this.processor = null;
      this.recorder = null;
      this.microphone = null;
      this.audioContext = null;
      this.noVolumeConsecutiveCount = 0;
      this.linkEnabled = false;
      this.countDownTimer = {
        count: 4,
        id: null,
      };

      if (fullReset) {
        this.mediaFile = null;
        this.selectedGroup = null;
        this.formValues.algorithm = null;
        this.formValues.title = '';
        this.formValues.submitted = false;
        this.formValues.feedbackRequested = false;
        this.durationTimer = {
          start: 0,
          count: 0,
          id: null,
        };
      }
    },
    recordClicked(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      // Prime sounds in Safari by playing silence. Safari complains but it works
      if (this.isSafari) {
        try {
          document.getElementById('buzzerNoise').play();
          document.getElementById('buzzerNoiseSource1').src = '/static/buzzer.ogg';
          document.getElementById('buzzerNoiseSource2').src = '/static/buzzer.mp3';
          document.getElementById('buzzerNoise').load();
        } catch (err) {
          // no-op
        }
      }

      this.recordingStatus = this.AUTHORIZING;
      // To avoid someone accidentally clicking the google doc, we delay the link from rendering for 1.5 seconds
      window.setTimeout(()=> {this.linkEnabled = true;}, 1500);

      this.initializeRecording();
    },
    stopRecording() {
      this.closeRecording();

      // Two different methods for building the wav file
      if (this.recordingMethod === 'custom') {
        this.recorder && this.recorder.exportWAV((wavData) => {
          this.recorder.clear();
          let wavFile = new File(
            [wavData], 'Recording.wav', {type: 'audio/wav', lastModified: Date.now()}
          );

          let reader = new window.FileReader();
          reader.readAsDataURL(wavFile);
          reader.onloadend = () => {
            this.mediaFile = reader.result;

            // Partial reset to clear audio context objects
            this.reset(this.STOPPED, false);
          };
        });
      } else {
        this.recordRTC.stopRecording(() => {
          let blob = this.recordRTC.getBlob();
          /*
          let type = 'audio/wav',
            fileName = 'newRec.wav';
          return window.download(file = new File([blob], fileName, {
            type
          }), fileName, type);
          */

          let reader = new window.FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            this.mediaFile = reader.result;

            // Partial reset to clear audio context objects
            this.reset(this.STOPPED, false);
          };
        });
      }

      this.populateAssignmentAndTitle();
      this.$announcer.set('Provide a title for your new recording');

      window.setTimeout(() => {
        try {
          if (!this.assignmentsEnabled) {
            this.$refs.recordingTitle.focus();
          }
        } catch(err) {
          // If the recording is too short, the title won't display and an error will be thrown
          // eslint-disable-line no-empty
        }
      }, 500);
    },
    stopClicked(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      // Update durationTimer.count ti be sure it's right
      const now = Math.round((new Date()).getTime() / 100);
      this.durationTimer.count = now - this.durationTimer.start;

      window.clearInterval(this.durationTimer.id);
      this.durationTimer.id = null;
      this.durationTimer.start = null;

      // Delay the termination until after analyzer has timed out
      this.recordingStatus = this.STOPPING;
      window.setTimeout(() => {
        this.stopRecording();
      }, 50);
    },
    cancelClicked() {
      if (window.confirm('Are you sure you want to delete this recording?')) {
        this.reset();
      }
    },
    nextClicked() {
      const recErrorHandler = (code=3) => {
        let notificationType = 'error';

        if (typeof code !== 'number') {
          // If we get an exception, log it to Sentry here and not via the notification
          notificationType = 'warning';

          if (window.Raven && code.message) {
            const message = code.message;
            window.Raven.captureMessage('Recording Error (Exception)', {tags: {message}});
          }
          code = 3;
        }

        this.$store.dispatch('notify', {
          type: notificationType,
          subject: `Recording Error (${code})`,
          text: `Error uploading your new recording.
                Did you disconnect from the Internet or WiFi? To retry uploading this
                recording, click the button below. To discard the recording and make a new
                one, click reload on your browser.<br><br>`,
          button: {
            action: () => {
              document.getElementById('closeNotificationButton').click();
              this.nextClicked();
            },
            text: 'Retry Upload',
          }
        });
      };

      // Validation
      if (!this.formValues.title && this.$refs.recordingTitle) {
        this.$refs.recordingTitle.focus();
        return;
      }
      if (this.mediaFile === null) {
        return recErrorHandler(2);
      }

      this.recordingStatus = this.UPLOADING;
      const formData = this.getFormData();

      service.post(formData)
        .then(recording => {
          const recordingId = recording.recordingId;
          if (!recordingId) {
            return recErrorHandler(4);
          }
          this.addRecordingToAssignment(recordingId);

          window.setTimeout(() => {
            this.$announcer.set(`Your recording is processing.
              Results may take up to 15 minutes to be available.
              An announcement will be made when the results are
              ready.`);
          }, 1000);

          return this.$store.dispatch('listRecordings')
            .then(() => {
              this.reset();
              this.recordingUploaded(recordingId, formData);
            });
        })
        .catch((response) => {
          let errorArg = 3;
          if (response && response[0]) {
            // Workaround to push the API error message into Sentry
            errorArg = { message: JSON.stringify(response[0]) };
          } else if (response && response.message) {
            // Push the javscript error message to Sentry
            errorArg = { message: response.message };
          }
          recErrorHandler(errorArg);
        });
    },
    startRecording() {
      this.recordingStatus = this.RECORDING;
      this.durationTimer.start = Math.round((new Date()).getTime() / 100);
      this.durationTimer.id = window.setInterval(this.intervalTick, 100);
      this.$nextTick(() => {
        document.getElementById('stopIconContainer') &&
        document.getElementById('stopIconContainer').focus();
      });
      if (this.recordingMethod === 'custom') {
        this.recorder = new Recorder(this.microphone, {
          numChannels: 1
        });
        this.recorder.record();
      } else {
        let options = {
          type: 'audio',
          numberOfAudioChannels: 1,
          checkForInactiveTracks: true,
          bufferSize: 16384,
          recorderType: RecordRTC.StereoAudioRecorder,
          desiredSampRate: 16000,
        };
        this.recordRTC = RecordRTC(window._audioStream, options);
        this.recordRTC.startRecording();
      }
    },
    startCountDown() {
      // Before initiating the countdown we trigger an API call to /me
      // as a way of ensuring they are logged in and have internet access.
      // This avoids the user making a recording they won't be able to upload.
      userApi.get()
        .then((data) => {
          if (data && data.userId) {
            this.recordingStatus = this.COUNT_DOWN;
            this.countDown();
          } else {
            return this.$router.push({ name: 'sign-in' });
          }
        })
        .catch(() => {
          return this.$router.push({ name: 'sign-in' });
        });
    },
    countDown() {
      this.countDownTimer.count--;
      if (this.countDownTimer.count) {
        this.$announcer.set(this.countDownTimer.count);
      }

      if (this.countDownTimer.count === 0) {
        this.countDownTimer.count = 4;
        return this.startRecording();
      }

      this.countDownTimer.id = window.setTimeout(() => {
        this.countDown();
      }, 1000);
    },
    showErrorScreen(errorCode) {
      window.clearInterval(this.durationTimer.id);
      this.durationTimer.id = null;
      this.durationTimer.start = null;

      const helpLink = this.helpLink;
      const text = (errorCode === this.AUTHORIZATION_ERROR) ?
        `You may need to modify your Microphone/Privacy Settings to grant your browser access to your microphone.
            To assist you with fixing your microphone settings, please review
            ${helpLink}
            ` :
        `Your browser was not able to access your microphone and was capturing only silence. Please ensure you have
            the right input device selected in your computer and browser settings then restart your browser. To assist you with fixing your microphone settings, please review
            ${helpLink}
            `;

      if (this.isEmbedded) {
        this.$store.dispatch('notify', {
          type: 'error',
          subject: 'Recording Error (1)',
          text,
        });
      } else {
        // Manually notify Sentry since we aren't using $store.dispatch
        let _text = text.substring(0, 200).replace(/\s/g, ' ');
        if (window.Raven) {
          window.Raven.captureMessage('Recording Error (1)', {tags: {_text}});
        }
      }

      if (this.countDownTimer.id) {
        window.clearTimeout(this.countDownTimer.id);
      }
      try {
        document.getElementById('buzzerNoise').play();
      } catch (err) {
        // no-op
      }
      this.closeRecording();
      this.reset(errorCode);
    },

    initializeRecording() {
      this.audioContext = new AudioContext();

      if (this.audioContext.createJavaScriptNode) {
        this.processor = this.audioContext.createJavaScriptNode(4096, 1, 1);
      } else if (this.audioContext.createScriptProcessor) {
        this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
      }
      this.processor.connect(this.audioContext.destination);

      const onMediaSuccess = (stream) => {
        window._audioStream = stream;

        this.startCountDown();

        /**
           * VOLUME METER
           */
        this.microphone = this.audioContext.createMediaStreamSource(stream);
        this.microphone.connect(this.processor);

        this.analyzer(this.audioContext);
      };

      navigator.mediaDevices.getUserMedia({video: false, audio: true})
        .then(onMediaSuccess)
        .catch(() => { this.showErrorScreen(this.AUTHORIZATION_ERROR); });
    }
  }
};
</script>
